import {
  getCustomDimensionWithStringValue
} from '../utils/custom-dimensions';

import { snakeCaseKeys } from 'utils/keys';

import CustomDimensionsDataProvider from './custom-dimensions-data-provider';

import { CUSTOM_DIMENSION_MAP } from '../constants/custom-dimensions';

import { CustomDimensions } from '../types/custom-dimensions';
import { GAEvent } from '../types/general';

declare global {
  interface Window {
    dataLayer?: Array<unknown>
  }
}

class GoogleAnalyticsProvider {
  code: null | string = null;

  gtag: null | ((...args: Array<any>) => void) = null;

  testModeAPI: {
    isEnabled: boolean,
    calls: Array<Array<string>>,
    resetCalls: () => void
  } = {
      isEnabled: false,
      calls: [],
      resetCalls() {
        this.calls = [];
      }
    };

  set(fields: { [i: string]: any }) {
    if (this.testModeAPI.isEnabled) {
      this.testModeAPI.calls.push(['set', snakeCaseKeys(fields)]);
      return;
    }

    if (this.gtag === null) {
      return;
    }

    this.gtag('set', snakeCaseKeys(fields));
  }

  event(event: GAEvent, cd: object) {
    if (this.testModeAPI.isEnabled) {
      this.set(cd);
      this.testModeAPI.calls.push(['event', event.eventAction, snakeCaseKeys(event)]);

      return;
    }

    if (this.gtag === null || this.code === null) {
      return;
    }

    /**
     *
     * https://jira.zalando.net/browse/COLLAB-13795
     *
     * Since we want to keep event names consostent between Web and Mobile
     * we must replace any separator with an "_" since Firebase does support only it.
     *
     */
    const { eventAction, ...rest } = event;
    const eventName = eventAction.replace(/[\s-\\/|.]/g, '_');

    this.gtag('event', eventName, snakeCaseKeys({
      ...{
        eventCategory: rest.eventCategory,
        eventLabel: rest.eventLabel
      },
      ...cd,
      sendTo: this.code
    }));
  }

  pageview(path: string, cds: object) {
    if (this.testModeAPI.isEnabled) {
      return;
    }

    if (this.gtag === null || this.code === null) {
      return;
    }

    this.gtag('event', 'page_view', snakeCaseKeys({
      pageLocation: `${location.protocol}//${location.host}${path}`,
      pagePath: path,
      sendTo: this.code,
      ...cds
    }));
  }

  canTrack() {
    return !this.testModeAPI.isEnabled && this.gtag !== null;
  }

  /**
    @deprecated
  */
  trackEvent(eventName: string, labelWithoutPrivateData: string | undefined = undefined) {
    if (!this.canTrack()) {
      return;
    }

    const names = eventName.split('|');
    const category = names.length === 3 ? names[1] : 'Common Events';
    const action = names[names.length - 1];

    this.event({
      eventCategory: category,
      eventAction: action,
      eventLabel: labelWithoutPrivateData
    }, {});
  }

  trackGAEvent(event: GAEvent, cd: Partial<CustomDimensions>): void {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line
      console.log('GA Event', event);
      // eslint-disable-next-line
      console.log('GA set dimensions', cd);
    }

    const prepared = getCustomDimensionWithStringValue(cd);

    this.event(event, {
      ...prepared
    });
  }

  trackPageView(path: string) {
    const prepared = getCustomDimensionWithStringValue(
      CustomDimensionsDataProvider.getGeneralHitScopeData()
    );

    this.pageview(path, { ...prepared });
  }

  init(code: string, testMode = false) {
    this.testModeAPI.isEnabled = testMode;

    if (testMode) {
      this.testModeAPI.calls.push(['create', code, 'auto']);
      return;
    }

    // Google Analytics 4
    window.dataLayer = window.dataLayer || [];
    this.gtag = function gtag() {
      if (!window.dataLayer) {
        return;
      }
      // It's crucial for GA... 2020 :facepalm
      /* eslint prefer-rest-params: "off" */
      window.dataLayer.push(arguments);
    };

    this.code = code;
    this.gtag('js', new Date());
    this.gtag('config', this.code, {
      custom_map: CUSTOM_DIMENSION_MAP,
      send_page_view: false
    });
  }

  getTestCalls(): Array<Array<string>> {
    return this.testModeAPI.calls;
  }

  resetTestCalls() {
    this.testModeAPI.resetCalls();
  }
}

export default new GoogleAnalyticsProvider();
